import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { skillsApi } from '../../../../../services/api';
import { useAlert } from '../../../../../contexts/AlertContext';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import EmptyState from '../../../../common/EmptyState';
import { FormSection } from '../../styles';
import { FormContainer } from './styles';
import AssignmentCardComponent from './AssignmentCard';
import { AssignmentBadgeFilter } from '../../../../common/filters';
import { 
  ASSIGNMENT_BADGES,
  getAssignmentStatusMyPerspective
} from '../../../../../constants/assignmentStatuses';

const Assignments = ({ employeeId }) => {
  const { showAlert } = useAlert();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBadges, setSelectedBadges] = useState([
    ASSIGNMENT_BADGES.ACTION_REQUIRED,
    ASSIGNMENT_BADGES.IN_PROGRESS
  ]);

  useEffect(() => {
    if (employeeId) {
      fetchAssignments();
    }
  }, [employeeId]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const data = await skillsApi.getEmployeeAssignments(employeeId);
      setAssignments(data);
    } catch (error) {
      showAlert(error.message || 'Failed to fetch assignments', 'error', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBadgeToggle = (badge) => {
    setSelectedBadges(prev => {
      if (prev.includes(badge)) {
        return prev.filter(b => b !== badge);
      } else {
        return [...prev, badge];
      }
    });
  };

  const filteredAssignments = assignments.filter(assignment => {
    if (selectedBadges.length === 0) return true;
    
    const assignmentBadge = getAssignmentStatusMyPerspective(assignment, employeeId);
    return selectedBadges.includes(assignmentBadge);
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <FormContainer>
      <FormSection>
        {assignments.length === 0 ? (
          <EmptyState
            title="No Assignments Found"
            description="This individual doesn't have any assignments yet."
          />
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <AssignmentBadgeFilter
                selectedBadges={selectedBadges}
                onBadgeToggle={handleBadgeToggle}
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                <strong>{filteredAssignments.length}</strong> of <strong>{assignments.length}</strong> {filteredAssignments.length === 1 ? 'assignment' : 'assignments'} 
                {selectedBadges.length > 0 && selectedBadges.length < Object.values(ASSIGNMENT_BADGES).length && (
                  <span> matching selected filters</span>
                )}
              </Typography>
            </Box>
            {filteredAssignments.length === 0 ? (
              <EmptyState
                title="No Matching Assignments"
                description="No assignments match the selected status filters. Try adjusting your filters."
              />
            ) : (
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                gap: 3
              }}>
                {filteredAssignments.map(assignment => (
                  <AssignmentCardComponent 
                    key={assignment.id} 
                    assignment={assignment}
                    employeeId={employeeId}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </FormSection>
    </FormContainer>
  );
};

export default Assignments; 